import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, Loader2 } from "lucide-react";
import { useCompanyIdentities } from "@/lib/useCompanyIdentities";
import CompanyLogo from "@/components/common/CompanyLogo";

/**
 * Staged analyze loader.
 *
 * `stage` is one of the keys below — null/undefined means "no stage info,
 * just show the generic loader". When a stage is set, all prior stages
 * render as completed (with a check) and the current stage shows a
 * pulsing spinner. This gives the user clear, honest progress feedback
 * without ever quoting a specific time estimate — analyses can vary by
 * model latency, ticker, and cache state, so an honest, evergreen copy
 * is more trustworthy than a number.
 *
 * The stages map to the backend `payload.stages` breakdown:
 *   fetching    → FMP / cache lookup
 *   valuation   → deterministic scoring engine
 *   ai_insights → OpenAI call (longest)
 *   finalizing  → post-processing + response serialization
 */
const STAGES = [
  { key: "fetching",    label: "Fetching market data" },
  { key: "valuation",   label: "Running valuation model" },
  { key: "ai_insights", label: "Generating AI insights" },
  { key: "finalizing",  label: "Finalizing analysis" },
];

export default function ScanLoader({
  ticker = "the stock",
  label = "Analyzing stock with AI…",
  stage = null,
}) {
  const activeIndex = stage ? STAGES.findIndex((s) => s.key === stage) : -1;

  // Phase 8.LP.LR — best-effort, cache-only identity lookup so we
  // can show the real company name + logo on the loading screen for
  // known tickers. NEVER blocks the analysis: if the cache is cold,
  // the loader keeps the pure ticker fallback (previous behaviour).
  // The hook seeds from the module-level identity cache, so a user
  // who just visited the landing page will see the AAPL/MSFT logo
  // instantly.
  const identityTickers = ticker && ticker !== "the stock" ? [ticker] : [];
  const { getIdentity } = useCompanyIdentities(identityTickers);
  const identity = identityTickers.length > 0 ? getIdentity(ticker) : null;
  const hasLogo = !!(identity && identity.logo_url);
  const displayName = identity && identity.name ? identity.name : null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-10 min-h-[280px] flex flex-col items-center justify-center text-center"
      data-testid="analyze-loading"
      data-identity-state={hasLogo ? "logo" : displayName ? "name" : "ticker-only"}
    >
      <div className="scan-sweep" />
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 backdrop-blur px-3 py-1 text-xs text-muted-foreground"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#3182CE]" />
        {label}
      </motion.div>
      {/* Company identity row — only rendered when we know the
          company. Logo tile matches the Analyze header aesthetic
          (bordered/contained, unmodified provider image). */}
      {identity && (hasLogo || displayName) ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.03 }}
          className="mt-4 inline-flex items-center gap-3"
          data-testid="analyze-loading-identity"
        >
          <CompanyLogo
            ticker={ticker}
            name={displayName}
            logoUrl={identity.logo_url}
            size="md"
            testId="analyze-loading-logo"
          />
          {displayName ? (
            <span className="font-medium text-foreground/90 truncate max-w-[280px] sm:max-w-none">
              {displayName}
            </span>
          ) : null}
        </motion.div>
      ) : null}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="mt-4 font-display text-2xl sm:text-3xl tracking-tight"
      >
        Stockaily is analyzing <span className="font-mono">{ticker}</span>…
      </motion.div>
      <div className="mt-1 text-sm text-muted-foreground">
        Generating AI-powered valuation insights · this usually takes around 30 seconds.
      </div>
      <div className="mt-6 w-full max-w-md grid grid-cols-1 gap-2.5 text-left">
        {STAGES.map((s, i) => {
          // No active stage yet → show the legacy "all pulsing" look so
          // we don't introduce a regression when the parent doesn't pass
          // a stage prop.
          const noStageProvided = activeIndex < 0;
          const done = !noStageProvided && i < activeIndex;
          const active = !noStageProvided && i === activeIndex;
          const pending = !noStageProvided && i > activeIndex;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.15 + i * 0.12 }}
              className="flex items-center gap-3 text-sm"
              data-testid={`analyze-stage-${s.key}`}
              data-stage-state={done ? "done" : active ? "active" : pending ? "pending" : "idle"}
            >
              {done ? (
                <span className="inline-flex w-4 h-4 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600">
                  <Check className="w-3 h-3" />
                </span>
              ) : active ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#3182CE]" />
              ) : pending ? (
                <span className="inline-flex w-4 h-4 items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                </span>
              ) : (
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[color:var(--app-glow-blue)] ml-1">
                  <span className="absolute inset-0 rounded-full bg-[color:var(--app-glow-blue)] animate-ping opacity-60" />
                </span>
              )}
              <span className={done ? "text-foreground/70" : active ? "text-foreground font-medium" : pending ? "text-muted-foreground/60" : "text-muted-foreground"}>
                {s.label}{active ? "…" : ""}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
